import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { gov } from "@/lib/governanceApi";
import { card, SectionTitle, Act } from "./ui";

export default function AuditLog() {
  const { data = [], refetch } = useQuery({ queryKey: ["gov-audit"], queryFn: () => gov.audit({ limit: 300 }) });
  const { data: keys } = useQuery({ queryKey: ["gov-audit-keys"], queryFn: gov.auditKeys });
  const verify = async () => {
    const r = await gov.verifyAudit();
    r.intact ? toast.success(`Chain intact — ${r.entries} entries verified`) : toast.error(`Chain broken at entry #${r.broken_at_seq}`);
    refetch();
  };
  return (
    <div>
      <div className="flex justify-between items-start gap-4">
        <SectionTitle sub="Append-only, HMAC-SHA256 hash-chained record of governance actions. There is no edit or delete function. Any change to a stored entry breaks verification from that entry onwards.">Audit & evidence trail</SectionTitle>
        <Act onClick={verify} data-testid="audit-verify">Verify chain</Act>
      </div>
      {keys && (
        <div className={`${card} p-4 mb-4 text-xs space-y-2`} data-testid="audit-key-ring">
          <div className="font-bold text-sm">Signing keys (identifiers only — never the secret)</div>
          <div className="flex flex-wrap gap-2">
            {keys.keys.map((k) => (
              <span key={k.key_id} className={`font-mono border px-2 py-1 ${k.current ? "border-emerald-500 bg-emerald-50" : "border-zinc-300"}`}>
                {k.key_id} · {k.source}{k.current ? " · current" : ""}
              </span>
            ))}
          </div>
          {!keys.dedicated_secret_configured && <div className="text-amber-700" data-testid="audit-key-warning">Entries are signed with the session secret (JWT_SECRET). Add a dedicated AUDIT_CHAIN_SECRET before rotating JWT_SECRET.</div>}
          <ol className="list-none text-zinc-600 space-y-0.5">{keys.procedure.map((p) => <li key={p}>{p}</li>)}</ol>
        </div>
      )}
      <div> className={`${card} overflow-auto`} data-testid="audit-table">
        <table className="w-full text-xs">
          <thead className="bg-[#14304F] text-white text-left"><tr>{["#", "When (UTC)", "Actor", "Action", "Entity", "Details", "Hash"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>
            {data.map((e) => (
              <tr key={e.seq} className="border-b border-zinc-100 align-top">
                <td className="px-3 py-2 font-mono">{e.seq}</td>
                <td className="px-3 py-2 font-mono whitespace-nowrap">{e.ts.slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2">{e.actor}</td>
                <td className="px-3 py-2 font-bold">{e.action}</td>
                <td className="px-3 py-2 font-mono">{e.entity_type}:{String(e.entity_id).slice(0, 8)}</td>
                <td className="px-3 py-2 font-mono text-[10px] max-w-md break-all">{JSON.stringify(e.details)}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{e.hash.slice(0, 12)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
